import { useMutation, useQuery } from '@apollo/client';
import {
  AppContext,
  toast,
  getTranslatedText,
  withErrorBoundary,
  Loader,
  NoDataCard,
  getBrowserType,
  getDownloadURL,
  EmptyTabState
} from '@nile/components';
import {
  ADD_NOTE_WORKITEM,
  DELETE_NOTE_WORKITEM,
  GET_NOTE_LIST,
  UPDATE_NOTE_WORKITEM
} from '@nile/graphql';
import { NoteBubble } from '@nile/mangifera/src/Components/Chat';
import { ActionContainer } from '@nile/mangifera/src/Containers/ChatContainers';
import { cloneDeep } from 'lodash';
import envConfig from '@nile/home/src/Config/env';
import React, { Component, useContext, useRef, useState } from 'react';
import { ChatFeed } from 'react-bell-chat';
import { FormattedMessage, injectIntl } from 'react-intl';

import styled from 'styled-components';

const StyledConversation = styled.div`
  border-bottom: 1px solid #e7e7e7;
  ${(props) =>
    props.dataAvailable &&
    `background-image: url(${envConfig.imageRoot}svg/chatBg.svg);`}
  background-size: 360px;
  // background-color: #f9f9ff;
  overflow: auto;
  .react-bell-chat__chat-scroll-area {
    //scroll-behavior: smooth;
  }
  .react-bell-chat__chat-panel {
    height: calc(100% - 90px); //action container height with margin
    overflow: auto;
  }
  .Polaris-Card {
    margin: 20px;
  }
  .rdw-editor-main {
    max-height: 200px;
    overflow: auto;
    margin: 10px;
    padding: 0 !important;
  }
  img {
    max-width: 100%;
  }
  .bubbleContainer {
    margin-bottom: 30px !important;

    code {
      white-space: pre-wrap;
    }

    table {
      width: 100% !important;
      max-width: 100% !important;
    }
  }
`;

const EmptyStateHolder = styled.div.attrs({
  className: 'flex center jcenter'
})`
  height: calc(100% - 90px);
  overflow: auto;
`;

const FeedWrap = styled.div`
  overflow: auto;
  scroll-behavior: smooth;
  -ms-overflow-style: none;
  ::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
  ${(props) =>
    props.isSafari &&
    `
    .ql-editor {
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
    }
  `}
`;

const publicNotesMap = {
  TICKET: true,
  PROJECT: true
};

class NoteList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      editor: ''
    };
  }

  onMessageSubmit = (message, callback) => {
    const { addNote, workItem } = this.props;
    const {
      message: content,
      mentions,
      attachments,
      inlineImages,
      privacyType
    } = message;
    const note = {
      content: content,
      mentions: mentions,
      inlineImages: inlineImages,
      addAttachments: attachments?.map(
        ({ fileName, originalFileName, fileSize }) => {
          return { fileName, originalFileName, fileSize };
        }
      ),
      workItem: workItem,
      privacyType: privacyType
    };

    addNote({
      variables: {
        input: note
      }
    }).then(({ data }) => {
      const { createNote } = data;
      if (callback) callback(createNote);
    });
  };

  getAttachmentURL = (message, item) => {
    const { workItem } = this.props;
    const module = 'NOTE_ATTACHMENT';
    const details = {
      fileName: item?.fileName,
      note: {
        noteId: message?.id,
        workItem: workItem
      }
    };
    const url = getDownloadURL({ module, details });
    return url;
  };

  render() {
    const { editor } = this.state;
    const {
      intl,
      user,
      workItem,
      authors,
      updateNote,
      deleteNote,
      actions,
      placeholder,
      notes,
      disableEdit,
      disableSpit
    } = this.props;
    return (
      <>
        {notes.length ? (
          <ChatFeed
            customChatBubble={(props) => (
              <NoteBubble
                {...props}
                {...actions}
                disableOptions={disableEdit}
                allowEditDeleteNotes={!disableEdit}
                deleteNote={deleteNote}
                disableSpit={disableSpit}
                editNote={updateNote}
                module="NOTE"
                details={{ workItem: workItem }}
                uploadParams={{
                  module: 'NOTE_ATTACHMENT',
                  details: JSON.stringify({ workItem: workItem })
                }}
                getAttachmentURL={this.getAttachmentURL}
                disableSpit
                showPrivacyType
              />
            )}
            messages={notes}
            authors={authors}
            yourAuthorId={user.userId}
          />
        ) : (
          <EmptyStateHolder>
            <EmptyTabState
              iconName={this.props.emptyStateIcon || 'EmptyTabNotes'}
              title={
                this.props.emptyStateTitle || 'invoice.notes.emptyState.title'
              }
              description={
                this.props.emptyStateDescription ||
                'invoice.notes.emptyState.description'
              }
            />
          </EmptyStateHolder>
        )}
        {!disableEdit && (
          <div className="p20">
            <ActionContainer
              hideToolbar={false}
              enablePublic={publicNotesMap?.[this.props.module] || false}
              allowMentions
              template={editor}
              module="NOTE"
              details={{ workItem: workItem }}
              onMessageSubmit={this.onMessageSubmit}
              attachmentUploadUrl={`${envConfig.fileRoot}/upload`}
              uploadParams={{
                module: 'NOTE_ATTACHMENT',
                details: JSON.stringify({ workItem: workItem })
              }}
              placeholder={getTranslatedText(
                intl,
                placeholder || 'ticket.details.chat.pl.desc'
              )}
            />
          </div>
        )}
      </>
    );
  }
}

/**
 * @description
 * NotesContainer is used in invoice and quote, it can be used in all modules in future
 * @param {enum} module
 * @param {string} workItemId
 * above params are required fields
 */
function NotesContainer(props) {
  const { intl, workItemId, module } = props;
  const appContext = useContext(AppContext);
  const browserType = getBrowserType();
  const isSafari = browserType === 'Apple Safari';
  const chatFeedRef = React.createRef();

  const [editNoteId, setEditNoteId] = useState(null);
  const editNoteIdByRef = useRef(null);
  editNoteIdByRef.current = editNoteId;
  const { user } = appContext;
  const workItem = { workId: workItemId, module: module };

  const { loading, data, error } = useQuery(GET_NOTE_LIST, {
    // fetchPolicy: 'cache-and-network',
    variables: { input: { workItem: workItem } }
  });

  const readNoteCache = (cache) => {
    return cache.readQuery({
      query: GET_NOTE_LIST,
      variables: { input: { workItem: workItem } }
    });
  };

  const writeNoteCache = (cache, notes) => {
    cache.writeQuery({
      query: GET_NOTE_LIST,
      variables: { input: { workItem: workItem } },
      data: {
        getNoteList: notes
      }
    });
  };

  const [createNote] = useMutation(ADD_NOTE_WORKITEM, {
    onCompleted({ createNote }) {
      toast('success', 'ticket.updates.newNoteCreate.success');
    },
    update(cache, { data }) {
      const noteCache = readNoteCache(cache);
      const notes = [...noteCache.getNoteList, data.createNote];
      writeNoteCache(cache, notes);
    }
  });

  const [
    deleteNote,
    { loading: deleteNoteLoading, error: deleteNoteError }
  ] = useMutation(DELETE_NOTE_WORKITEM, {
    onCompleted({ res }) {
      toast('success', getTranslatedText(intl, 'ticket.note.delete.success'));
    },
    update(cache, { data }) {
      toast('success', getTranslatedText(intl, 'ticket.note.delete.success'));
      const deletedNoteId = data?.deleteNote;
      const noteCache = readNoteCache(cache);
      const notes = [...noteCache.getNoteList];
      const updatedNotes = notes.filter((el) => el.noteId !== deletedNoteId);
      writeNoteCache(cache, updatedNotes);
    }
  });

  const [
    editNote,
    { loading: editNoteLoading, error: editNoteError }
  ] = useMutation(UPDATE_NOTE_WORKITEM, {
    onCompleted({ res }) {
      // toast('success', getTranslatedText(intl, 'ticket.note.edit.success'));
      setEditNoteId(null);
    },
    update(cache, { data }) {
      const updateNote = data?.updateNote;
      const noteCache = readNoteCache(cache);
      const notes = cloneDeep(noteCache.getNoteList);
      const updatedNotes = notes.map((el) => {
        if (el.noteId === updateNote.noteId) {
          return {
            ...updateNote
          };
        }
        return el;
      });
      writeNoteCache(cache, updatedNotes);
    },
    onError() {
      // console.log(err)
    }
  });

  const actions = {
    editNoteId: editNoteIdByRef.current,
    setEditNoteId: setEditNoteId,
    editNoteLoading: editNoteLoading
  };

  const updateNote = ({ variables }) => {
    const { input } = variables;
    return editNote({
      variables: {
        input: {
          ...input,
          workItem: workItem
        }
      }
    });
  };

  const destroyNote = ({ variables }) => {
    const { input } = variables;
    deleteNote({
      variables: {
        input: {
          ...input,
          workItem: workItem
        }
      }
    });
  };

  if (loading) return <Loader />;
  if (error) return <>Error....</>;

  const authors = [];
  const notesData = data?.getNoteList || [];
  const notes = notesData?.map((note) => {
    authors.push({
      name: `${note.addedBy.firstName}  ${note.addedBy.lastName}`
    });
    return {
      message: note.content,
      id: note.noteId,
      isSend: true,
      authorId: note.addedBy.userId || 'system',
      user: note.addedBy,
      messageType: 'note',
      attachments: note?.attachments,
      type: 'note',
      workId: workItemId,
      time: note?.addedOn,
      data: note
    };
  });

  return (
    <StyledConversation
      dataAvailable={notesData.length}
      className="full-height"
    >
      <FeedWrap
        isSafari={isSafari}
        className="full-height relative"
        ref={chatFeedRef}
      >
        <NoteList
          {...props}
          user={user}
          notes={notes}
          authors={authors}
          workItem={workItem}
          addNote={createNote}
          updateNote={updateNote}
          deleteNote={destroyNote}
          actions={actions}
        />
      </FeedWrap>
    </StyledConversation>
  );
}

export default withErrorBoundary(injectIntl(NotesContainer));
